import React, { useState, useEffect, useRef } from 'react';
import { impactMetrics } from '../../data/mock';

// Animated counter hook
const useCountUp = (end, duration = 2000, startCounting) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!startCounting) return;
    
    let startTime;
    let animationFrame;
    
    // Parse the end value (remove + and # symbols, handle commas)
    const numericEnd = parseInt(end.replace(/[^0-9]/g, ''), 10);
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      
      setCount(Math.floor(easeOutQuart * numericEnd));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [end, duration, startCounting]);
  
  return count;
};

// Individual metric component
const MetricItem = ({ value, label, startCounting }) => {
  const isHashTag = value.startsWith('#');
  const hasPlus = value.includes('+');
  const count = useCountUp(value, 2000, startCounting);
  
  // Format the number with commas
  const formattedCount = count.toLocaleString();
  
  return (
    <div className="text-center px-8 py-6">
      {/* Large Number */}
      <div className="mb-4">
        <span className="text-6xl sm:text-7xl lg:text-8xl font-extralight tracking-tight text-stone-900">
          {isHashTag && <span className="text-5xl sm:text-6xl lg:text-7xl">#</span>}
          {formattedCount}
          {hasPlus && <span className="text-5xl sm:text-6xl lg:text-7xl">+</span>}
        </span>
      </div>
      
      {/* Label */}
      <span className="text-sm font-medium tracking-widest text-stone-400 uppercase">
        {label}
      </span>
    </div>
  );
};

export const ImpactMetrics = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Clean grid with vertical separators */}
        <div className="grid grid-cols-1 md:grid-cols-3 relative">
          {impactMetrics.map((metric, index) => (
            <div key={index} className="relative">
              <MetricItem 
                value={metric.value} 
                label={metric.label} 
                startCounting={isVisible}
              />
              
              {/* Vertical separator - hidden on last item and mobile */}
              {index < impactMetrics.length - 1 && (
                <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 w-px h-20 bg-stone-200" />
              )}
              
              {/* Horizontal separator for mobile */}
              {index < impactMetrics.length - 1 && (
                <div className="md:hidden mx-auto w-24 h-px bg-stone-200 my-4" />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
